import React, { useState, useEffect, useCallback } from 'react';
import { ArrowRight, Award, Flame, ChevronLeft, ChevronRight, ChevronDown, ChevronRight as ChevronRightIcon, Loader2, FileCheck2, ShieldCheck, Zap } from 'lucide-react';
import { Tooltip } from 'antd';
import { useTheme } from '../../../contexts/ThemeContext';
import { Avatar } from '../../DashboardV2/components/primitives';
import * as goalsApi from '../../../services/goalsPerformanceApi';

const formatDate = (date) => {
  if (!date) return null;
  try {
    const d = new Date(date);
    return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
  } catch {
    return null;
  }
};

const StageIcon = ({ Icon, pendingLabel, doneLabel, done, isDark }) => {
  // Inline styles — `emerald` is overridden in tailwind.config.js to a single
  // hex value, so the default emerald scale (bg-emerald-100 etc.) doesn't exist.
  const style = done
    ? (isDark
        ? { background: 'rgba(16,185,129,0.15)', color: '#6EE7B7', borderColor: 'rgba(16,185,129,0.40)' }
        : { background: '#D1FAE5', color: '#047857', borderColor: '#A7F3D0' })
    : (isDark
        ? { background: 'rgba(255,255,255,0.04)', color: 'rgba(255,255,255,0.40)', borderColor: 'rgba(255,255,255,0.10)' }
        : { background: '#F1F5F9', color: '#64748B', borderColor: '#E2E8F0' });
  return (
    <Tooltip title={done ? `${doneLabel} ✓` : pendingLabel}>
      <span
        className="inline-flex items-center justify-center w-7 h-7 rounded-full border"
        style={style}
      >
        <Icon size={13} strokeWidth={2.25} />
      </span>
    </Tooltip>
  );
};

const StatusStages = ({ member, isDark }) => {
  const total = parseInt(member.totalGoals ?? 0, 10) || 0;
  const approved = parseInt(member.goalsCount ?? 0, 10) || 0;
  const evaluated = parseInt(member.evaluatedGoalsCount ?? 0, 10) || 0;
  const submittedDone = member.goalsSubmitted === true;
  const reviewDone = total > 0 && approved >= total;
  const evalDone = total > 0 && evaluated >= total;

  return (
    <div className="inline-flex items-center gap-1.5">
      <StageIcon
        Icon={FileCheck2}
        pendingLabel="Goals Not Submitted"
        doneLabel="Goals Submitted"
        done={submittedDone}
        isDark={isDark}
      />
      <span className={`w-2 h-px ${isDark ? 'bg-white/15' : 'bg-slate-200'}`} />
      <StageIcon
        Icon={ShieldCheck}
        pendingLabel="Pending Review"
        doneLabel="Review Completed"
        done={reviewDone}
        isDark={isDark}
      />
      <span className={`w-2 h-px ${isDark ? 'bg-white/15' : 'bg-slate-200'}`} />
      <StageIcon
        Icon={Zap}
        pendingLabel="Awaiting Evaluation"
        doneLabel="Evaluation Completed"
        done={evalDone}
        isDark={isDark}
      />
    </div>
  );
};

const ScoreCell = ({ score, isDark }) => {
  const n = parseFloat(score);
  const safe = isNaN(n) ? 0 : n;
  const color = safe >= 7
    ? (isDark ? 'text-emerald-400' : 'text-emerald-600')
    : safe >= 3
      ? (isDark ? 'text-amber-400' : 'text-amber-600')
      : (isDark ? 'text-rose-400' : 'text-rose-600');
  return <span className={`font-bold ${color}`}>{safe.toFixed(1)}</span>;
};

const BadgeCell = ({ member, badgeRanges, isDark }) => {
  const [imgErr, setImgErr] = useState(false);
  const label = member.badge || member.status || '';
  let iconUrl = null;
  if (label && badgeRanges && badgeRanges.length > 0) {
    let bRange = badgeRanges.find(r => (r.label || '').toLowerCase() === label.toLowerCase());
    if (!bRange) {
      bRange = badgeRanges.find(r =>
        label.toLowerCase().includes((r.label || '').toLowerCase()) ||
        (r.label || '').toLowerCase().includes(label.toLowerCase())
      );
    }
    if (bRange?.icon?.url && typeof bRange.icon.url === 'string' && bRange.icon.url.trim()) {
      iconUrl = bRange.icon.url;
    }
  }

  useEffect(() => { setImgErr(false); }, [iconUrl]);

  if (!member.hasBadge) return null;

  return (
    <Tooltip title={member.badge || member.status || 'Badge earned'}>
      <div className={`w-7 h-7 rounded-lg flex items-center justify-center shrink-0 overflow-hidden ${isDark ? 'bg-amber-500/15 border border-amber-500/30' : 'bg-amber-50 border border-amber-200'}`}>
        {iconUrl && !imgErr ? (
          <img src={iconUrl} alt="badge" className="w-full h-full object-cover" onError={() => setImgErr(true)} />
        ) : (
          <Award size={14} className={isDark ? 'text-amber-400' : 'text-amber-600'} fill="currentColor" />
        )}
      </div>
    </Tooltip>
  );
};

const MemberRow = ({
  member,
  idx,
  depth,
  badgeRanges,
  cycleEndDate,
  cycleId,
  onMemberClick,
  expandedIds,
  onToggleExpand,
  loadingReportees,
  reporteesByMember,
  isDark,
  styles,
  showHierarchy,
}) => {
  const expanded = expandedIds.has((member._id || member.id || idx).toString());
  const { primaryText, subtleText, secondaryText, rowHover } = styles;
  const dueDateLabel = formatDate(cycleEndDate);

  const id = (member._id || member.id || idx).toString();
  const designation = member.role || member.designation || '—';
  const goals = member.goals || (member.totalGoals != null ? `${member.goalsCount ?? 0}/${member.totalGoals}` : '0/0');
  const streak = (member.streak || '').toString().split(' ')[0];
  const dueRaw = member.reviewEnd || member.dueDate || cycleEndDate;
  const dueLabel = formatDate(dueRaw) || dueDateLabel || '—';
  const isUrgent = (() => {
    if (!dueRaw) return false;
    try {
      const d = new Date(dueRaw);
      const now = new Date();
      const days = Math.ceil((d - now) / (1000 * 60 * 60 * 24));
      return days >= 0 && days <= 3;
    } catch { return false; }
  })();

  const score = member.score ?? member.currentScore ?? member.avgScore;
  const status = member.status ?? (member.goalsSubmitted ? 'Submitted' : 'Not Submitted');
  const hasReportees = showHierarchy && member.hasReportees === true;
  const isLoading = loadingReportees.has(id);
  const childMembers = reporteesByMember.get(id) || [];

  // Indentation per nesting level — wider so the tree guide reads cleanly
  const STEP = 28;
  const indentPx = depth * STEP;
  const isNested = depth > 0;
  const guideColor = isDark ? 'rgba(255,255,255,0.12)' : 'rgba(15,23,42,0.10)';

  // Subtle alternating background for nested rows so a sub-tree visually clusters
  const nestedRowBg = isNested
    ? (isDark ? 'bg-white/[0.015]' : 'bg-slate-50/60')
    : '';

  return (
    <>
      <tr
        onClick={() => onMemberClick && onMemberClick(member)}
        className={`group cursor-pointer transition-all ${nestedRowBg} ${rowHover}`}
      >
        <td className="px-5 py-4">
          <div className="flex items-center gap-2 min-w-0 relative" style={{ paddingLeft: indentPx }}>
            {/* Tree guide lines: a vertical line for each ancestor depth + an L-bracket
                connecting this row to its parent. Pure CSS, no extra DOM per ancestor. */}
            {isNested && (
              <>
                {Array.from({ length: depth }).map((_, level) => {
                  const left = level * STEP + 12; // center of the chevron column
                  const isLastSegment = level === depth - 1;
                  return (
                    <span
                      key={`guide-${level}`}
                      aria-hidden
                      className="absolute pointer-events-none"
                      style={{
                        left,
                        top: 0,
                        bottom: 0,
                        width: 1,
                        background: guideColor,
                      }}
                    >
                      {isLastSegment && (
                        <span
                          className="absolute"
                          style={{
                            left: 0,
                            top: '50%',
                            width: STEP - 12,
                            height: 1,
                            background: guideColor,
                          }}
                        />
                      )}
                    </span>
                  );
                })}
              </>
            )}

            {showHierarchy && (
              hasReportees ? (
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); onToggleExpand(id); }}
                  className={`w-6 h-6 flex items-center justify-center rounded-md transition-all shrink-0 relative z-[1] ${
                    isDark ? 'bg-white/[0.04] hover:bg-white/10 text-white/70' : 'bg-white hover:bg-slate-100 text-slate-500 border border-slate-200'
                  }`}
                  aria-label={expanded ? 'Collapse reportees' : 'Expand reportees'}
                >
                  {isLoading
                    ? <Loader2 size={14} className="animate-spin" />
                    : expanded
                      ? <ChevronDown size={14} />
                      : <ChevronRightIcon size={14} />}
                </button>
              ) : (
                <span className="w-6 h-6 shrink-0" />
              )
            )}

            <span className="shrink-0 relative z-[1]">
              <Avatar user={member} name={member.name || '?'} size={36} />
            </span>
            <div className="flex flex-col min-w-0">
              <div className="flex items-center gap-2 min-w-0">
                <span className={`font-bold leading-tight truncate ${primaryText}`}>
                  {member.name || '—'}
                </span>
                <BadgeCell member={member} badgeRanges={badgeRanges} isDark={isDark} />
                {streak && parseInt(streak, 10) > 0 && (
                  <Tooltip title={`${streak} month streak`}>
                    <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-bold ${isDark ? 'bg-orange-500/15 text-orange-300' : 'bg-orange-50 text-orange-600'}`}>
                      <Flame size={10} className="fill-current" />
                      {streak}
                    </span>
                  </Tooltip>
                )}
              </div>
              {member.email && (
                <span className={`text-[11.5px] font-medium truncate ${subtleText}`}>
                  {member.email}
                </span>
              )}
            </div>
          </div>
        </td>
        <td className={`px-5 py-4 whitespace-nowrap ${secondaryText}`}>{designation}</td>
        <td className={`px-5 py-4 ${subtleText}`}>{goals}</td>
        <td className="px-5 py-4"><ScoreCell score={score} isDark={isDark} /></td>
        <td className="px-5 py-4 whitespace-nowrap"><StatusStages member={member} isDark={isDark} /></td>
        <td className="px-5 py-4 whitespace-nowrap">
          <span className={`font-medium ${isUrgent ? (isDark ? 'text-rose-400' : 'text-rose-600') : subtleText}`}>
            {dueLabel}
          </span>
        </td>
        <td className="px-5 py-4 text-right">
          <button
            onClick={(e) => { e.stopPropagation(); onMemberClick && onMemberClick(member); }}
            className={`font-bold text-[13px] inline-flex items-center justify-end gap-1 ml-auto transition-all opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 ${isDark ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-700'} hover:underline`}
          >
            View Goals <ArrowRight size={14} />
          </button>
        </td>
      </tr>

      {expanded && childMembers.length === 0 && !isLoading && (
        <tr className={isDark ? 'bg-white/[0.015]' : 'bg-slate-50/60'}>
          <td colSpan={7} className={`px-5 py-3 text-[12px] italic ${subtleText}`} style={{ paddingLeft: indentPx + STEP + 56 }}>
            No reportees.
          </td>
        </tr>
      )}

      {expanded && childMembers.map((child, cidx) => (
        <MemberRow
          key={child._id || child.id || `${id}-${cidx}`}
          member={child}
          idx={cidx}
          depth={depth + 1}
          badgeRanges={badgeRanges}
          cycleEndDate={cycleEndDate}
          cycleId={cycleId}
          onMemberClick={onMemberClick}
          expandedIds={expandedIds}
          onToggleExpand={onToggleExpand}
          loadingReportees={loadingReportees}
          reporteesByMember={reporteesByMember}
          isDark={isDark}
          styles={styles}
          showHierarchy={showHierarchy}
        />
      ))}
    </>
  );
};

const TeamMembersTable = ({
  members = [],
  badgeRanges = [],
  cycleEndDate = null,
  cycleId = null,
  showHierarchy = false,
  onMemberClick,
  pagination = { total: 0, totalPages: 1 },
  currentPage = 1,
  pageSize = 12,
  onPageChange,
}) => {
  const { mode } = useTheme();
  const isDark = mode === 'dark';

  // Tree expansion state
  const [expandedIds, setExpandedIds] = useState(() => new Set());
  const [loadingReportees, setLoadingReportees] = useState(() => new Set());
  const [reporteesByMember, setReporteesByMember] = useState(() => new Map());

  // Collapse all expansions when hierarchy mode is turned off so re-enabling
  // starts from a clean state.
  useEffect(() => {
    if (!showHierarchy) {
      setExpandedIds(new Set());
    }
  }, [showHierarchy]);

  const handleToggleExpand = useCallback(async (memberId) => {
    const idStr = memberId.toString();
    const isOpen = expandedIds.has(idStr);

    if (isOpen) {
      setExpandedIds(prev => {
        const next = new Set(prev);
        next.delete(idStr);
        return next;
      });
      return;
    }

    // Open: fetch if we haven't already
    if (!reporteesByMember.has(idStr)) {
      setLoadingReportees(prev => new Set(prev).add(idStr));
      try {
        const res = await goalsApi.getMemberReportees(idStr, cycleId);
        const data = (res && res.success && Array.isArray(res.data)) ? res.data : [];
        setReporteesByMember(prev => {
          const next = new Map(prev);
          next.set(idStr, data);
          return next;
        });
      } catch (err) {
        console.error('Failed to fetch reportees for', idStr, err);
        setReporteesByMember(prev => {
          const next = new Map(prev);
          next.set(idStr, []);
          return next;
        });
      } finally {
        setLoadingReportees(prev => {
          const next = new Set(prev);
          next.delete(idStr);
          return next;
        });
      }
    }

    setExpandedIds(prev => new Set(prev).add(idStr));
  }, [expandedIds, reporteesByMember, cycleId]);

  const surface = isDark ? 'bg-white/[0.03] border-white/10' : 'bg-white border-slate-200';
  const headRowBg = isDark ? 'bg-white/[0.04]' : 'bg-slate-50';
  const headText = isDark ? 'text-white/60' : 'text-slate-500';
  const rowDivider = isDark ? 'divide-white/5' : 'divide-slate-100';
  const rowHover = isDark ? 'hover:bg-white/[0.04]' : 'hover:bg-slate-50';
  const primaryText = isDark ? 'text-white' : 'text-slate-900';
  const subtleText = isDark ? 'text-white/50' : 'text-slate-500';
  const secondaryText = isDark ? 'text-white/70' : 'text-slate-700';

  const styles = { primaryText, subtleText, secondaryText, rowHover };

  return (
    <div
      className={`rounded-xl border shadow-sm overflow-hidden ${surface}`}
      style={{ fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif' }}
    >
      <div className={`px-5 py-4 border-b ${isDark ? 'border-white/10' : 'border-slate-200'} flex items-center justify-between`}>
        <h3 className={`text-[15px] font-bold tracking-tight ${primaryText}`}>Team Member Report</h3>
        <span className={`text-[11px] font-bold uppercase tracking-widest ${subtleText}`}>
          {pagination.total || members.length} {(pagination.total || members.length) === 1 ? 'member' : 'members'}
        </span>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full border-collapse text-[13px]">
          <thead>
            <tr className={`${headRowBg} border-b ${isDark ? 'border-white/10' : 'border-slate-200'}`}>
              <th className={`px-5 py-3 text-left font-bold uppercase text-[11px] tracking-widest ${headText} w-1/4`}>Member</th>
              <th className={`px-5 py-3 text-left font-bold uppercase text-[11px] tracking-widest ${headText}`}>Designation</th>
              <th className={`px-5 py-3 text-left font-bold uppercase text-[11px] tracking-widest ${headText}`}>Goals</th>
              <th className={`px-5 py-3 text-left font-bold uppercase text-[11px] tracking-widest ${headText}`}>Score</th>
              <th className={`px-5 py-3 text-left font-bold uppercase text-[11px] tracking-widest ${headText}`}>Status</th>
              <th className={`px-5 py-3 text-left font-bold uppercase text-[11px] tracking-widest ${headText}`}>Due Date</th>
              <th className={`px-5 py-3 text-right font-bold uppercase text-[11px] tracking-widest ${headText}`}>Action</th>
            </tr>
          </thead>
          <tbody className={`divide-y ${rowDivider}`}>
            {members.length === 0 ? (
              <tr>
                <td colSpan={7} className={`px-5 py-12 text-center text-sm ${subtleText}`}>
                  No team members found.
                </td>
              </tr>
            ) : members.map((member, idx) => (
              <MemberRow
                key={member._id || member.id || idx}
                member={member}
                idx={idx}
                depth={0}
                badgeRanges={badgeRanges}
                cycleEndDate={cycleEndDate}
                cycleId={cycleId}
                onMemberClick={onMemberClick}
                expandedIds={expandedIds}
                onToggleExpand={handleToggleExpand}
                loadingReportees={loadingReportees}
                reporteesByMember={reporteesByMember}
                isDark={isDark}
                styles={styles}
                showHierarchy={showHierarchy}
              />
            ))}
          </tbody>
        </table>
      </div>

      {onPageChange && pagination.total > pageSize && (
        <div className={`flex items-center justify-between px-5 py-4 border-t flex-wrap gap-3 ${isDark ? 'border-white/10' : 'border-slate-200'}`}>
          <span className={`text-[12px] ${subtleText}`}>
            Showing {((currentPage - 1) * pageSize) + 1}–{Math.min(currentPage * pageSize, pagination.total)} of {pagination.total}
          </span>
          <div className="flex items-center gap-1">
            <button
              onClick={() => onPageChange(currentPage - 1)}
              disabled={currentPage <= 1}
              className={`w-8 h-8 flex items-center justify-center rounded-lg text-sm transition-all ${
                currentPage <= 1
                  ? 'opacity-30 cursor-not-allowed'
                  : isDark ? 'hover:bg-white/10 text-white/70' : 'hover:bg-slate-100 text-slate-500'
              }`}
            >
              <ChevronLeft size={16} />
            </button>
            {(() => {
              const total = pagination.totalPages;
              const cur = currentPage;
              let pages = [];
              if (total <= 5) {
                pages = Array.from({ length: total }, (_, i) => i + 1);
              } else {
                pages = [1];
                if (cur > 3) pages.push('...');
                for (let i = Math.max(2, cur - 1); i <= Math.min(total - 1, cur + 1); i++) pages.push(i);
                if (cur < total - 2) pages.push('...');
                pages.push(total);
              }
              return pages.map((p, i) =>
                p === '...' ? (
                  <span key={`e-${i}`} className={`w-8 h-8 flex items-center justify-center text-sm ${isDark ? 'text-white/30' : 'text-slate-400'}`}>…</span>
                ) : (
                  <button
                    key={p}
                    onClick={() => onPageChange(p)}
                    className={`w-8 h-8 flex items-center justify-center rounded-lg text-sm font-semibold transition-all ${
                      cur === p
                        ? 'bg-blue-600 !text-white shadow-sm'
                        : isDark ? 'text-white/60 hover:bg-white/10' : 'text-slate-600 hover:bg-slate-100'
                    }`}
                  >
                    {p}
                  </button>
                )
              );
            })()}
            <button
              onClick={() => onPageChange(currentPage + 1)}
              disabled={currentPage >= pagination.totalPages}
              className={`w-8 h-8 flex items-center justify-center rounded-lg text-sm transition-all ${
                currentPage >= pagination.totalPages
                  ? 'opacity-30 cursor-not-allowed'
                  : isDark ? 'hover:bg-white/10 text-white/70' : 'hover:bg-slate-100 text-slate-500'
              }`}
            >
              <ChevronRight size={16} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default TeamMembersTable;
